"use client";

import { Fragment, useState, ChangeEvent } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import { Avatar } from "@heroui/react";
import { IconCircleLetterS, IconChevronDown, IconX } from "@tabler/icons-react";
import {
  createSetItems,
  filterSets,
} from "@/lib/games/unionArena/setFilterHelper";
import { getGameHandler } from "@/games";

interface UASetFilterProps {
  selected: string | null;
  setSelected: (setId: string | null) => void;
  label?: string;
  width?: string;
  sortByName?: boolean;
}

export default function UASetFilter({
  selected,
  setSelected,
  label = "Set",
  width = "200px",
  sortByName = true,
}: UASetFilterProps) {
  const [query, setQuery] = useState("");
  const initialItems = createSetItems(sortByName);

  const [items, setItems] = useState(initialItems);
  const filteredItems = query === "" ? items : filterSets(items, query);

  let gameHandler = getGameHandler("union-arena");

  return (
    <div className={`w-[${width}]`}>
      <Combobox value={selected} onChange={setSelected}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg text-left shadow-md sm:text-sm">
            <div className="flex items-center rounded-l-lg bg-default-100">
              <IconCircleLetterS
                size={22}
                className="text-gray-300 ml-3 min-w-[22px]"
              />
              <ComboboxInput
                className="w-full h-[32px] border-none py-2 pl-2 pr-10 text-sm leading-5 text-gray-300 bg-default-100 focus:outline-none"
                displayValue={(setId: string) => {
                  const item = items.find((i) => i.id === setId);
                  return item?.name || "";
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setQuery(event.target.value);
                }}
                placeholder={label}
              />
              {selected && (
                <button
                  type="button"
                  className="absolute right-8 flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors bg-default-100 rounded-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelected(null);
                  }}
                >
                  <IconX size={14} />
                </button>
              )}
            </div>
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <IconChevronDown
                className="text-gray-300"
                height={16}
                width={16}
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-default-100 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {filteredItems.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-300">
                  Nothing found.
                </div>
              ) : (
                filteredItems.map((item) => (
                  <ComboboxOption
                    key={item.id}
                    className={({ active }: { active: boolean }) =>
                      `relative cursor-default select-none py-2 px-2 ${
                        active
                          ? "bg-default-200 text-white rounded-md"
                          : "text-gray-300"
                      }`
                    }
                    value={item.id}
                  >
                    {({
                      selected,
                      active,
                    }: {
                      selected: boolean;
                      active: boolean;
                    }) => (
                      <div className="flex items-center gap-2">
                        <Avatar
                          alt={item.name}
                          className="flex-shrink-0 rounded-[4px]"
                          size="sm"
                          radius="none"
                          src={gameHandler.getSetImage(item.id)}
                        />
                        <div className="flex flex-col">
                          <span
                            className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {item.name}
                          </span>
                          <span className="text-xs text-gray-400">
                            {item.id}
                          </span>
                        </div>
                      </div>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
